import type { ValueProcessorByRule } from '../../types/index.noReact';
import { isValidValue, mongoOperators, shouldRenderAsNumber, toArray, trimIfString } from './utils';

export const defaultValueProcessorMongoDBByRule: ValueProcessorByRule = (
  { field, operator, value, valueSource, fieldType, disabled },
  // istanbul ignore next
  { escapeQuotes, parseNumbers } = {}
) => {
  if (disabled) return '';

  const escapeDoubleQuotes = (v: any) =>
    typeof v !== 'string' || !escapeQuotes ? v : v.replaceAll(`"`, `\\"`);
  const convertSpecial = (v: any, type: any) =>
    typeof v !== 'string'
      ? v
      : type === 'date'
      ? `ISODate("${v}")`
      : type === 'objectId'
      ? `ObjectId("${v}")`
      : type === 'regex'
      ? `/${v}/i`
      : type === 'object'
      ? `${v}`
      : type === 'array'
      ? `${v}`
      : `"${v}"`;
  const valueIsField = valueSource === 'field';
  const useBareValue =
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint' ||
    shouldRenderAsNumber(value, parseNumbers);
  if (
    operator === '<' ||
    operator === '<=' ||
    operator === '!=' ||
    operator === '>' ||
    operator === '>='
  ) {
    const mongoOperator = mongoOperators[operator];
    return `"${field}":{"${mongoOperator}":${
      useBareValue ? trimIfString(value) : convertSpecial(value, fieldType)
    }}`;
  } else if (operator === '=') {
    return `"${field}":${useBareValue ? trimIfString(value) : convertSpecial(value, fieldType)}`;
  } else if (operator === 'contains') {
    return valueIsField
      ? `"$where":"this.${field}.includes(this.${value})"`
      : `"${field}":{"$regex":/${escapeDoubleQuotes(value)}/i}`;
  } else if (operator === 'beginsWith') {
    return valueIsField
      ? `"$where":"this.${field}.startsWith(this.${value})"`
      : `"${field}":{"$regex":/^${escapeDoubleQuotes(value)}/i}`;
  } else if (operator === 'endsWith') {
    return valueIsField
      ? `"$where":"this.${field}.endsWith(this.${value})"`
      : `"${field}":{"$regex":/${escapeDoubleQuotes(value)}$/i}`;
  } else if (operator === 'doesNotContain') {
    return valueIsField
      ? `"$where":"!this.${field}.includes(this.${value})"`
      : `"${field}":{"$not":{"$regex":/${escapeDoubleQuotes(value)}/i}}`;
  } else if (operator === 'doesNotBeginWith') {
    return valueIsField
      ? `"$where":"!this.${field}.startsWith(this.${value})"`
      : `"${field}":{"$not":{"$regex":/^${escapeDoubleQuotes(value)}/i}}`;
  } else if (operator === 'doesNotEndWith') {
    return valueIsField
      ? `"$where":"!this.${field}.endsWith(this.${value})"`
      : `"${field}":{"$not":{"$regex":/${escapeDoubleQuotes(value)}$/i}}`;
  } else if (operator === 'null') {
    return `"${field}":null`;
  } else if (operator === 'exist') {
    return `"${field}":{$exists:true}`;
  } else if (operator === 'notExist') {
    return `"${field}":{$exists:false}`;
  } else if (operator === 'notNull') {
    return `"${field}":{"$ne":null}`;
  } else if (operator === 'in' || operator === 'notIn') {
    const valArray = toArray(value);
    return `"${field}":{"${mongoOperators[operator]}":[${valArray
      .map(val =>
        shouldRenderAsNumber(val, parseNumbers)
          ? `${trimIfString(val)}`
          : `"${escapeDoubleQuotes(val)}"`
      )
      .join(',')}]}`;
  } else if (operator === 'between' || operator === 'notBetween') {
    const valArray = toArray(value);
    if (isValidValue(valArray[0]) && isValidValue(valArray[1])) {
      const [first, second] = valArray;
      const firstNum = shouldRenderAsNumber(first, true) ? parseFloat(first) : NaN;
      const secondNum = shouldRenderAsNumber(second, true) ? parseFloat(second) : NaN;
      const firstValue = !isNaN(firstNum)
        ? `${first}`
        : first instanceof Object
        ? JSON.stringify(first)
        : `"${escapeDoubleQuotes(first) ?? ''}"`;
      const secondValue = !isNaN(secondNum)
        ? `${second}`
        : second instanceof Object
        ? JSON.stringify(second)
        : `"${escapeDoubleQuotes(second) ?? ''}"`;
      if (operator === 'between') {
        return `"${field}":{"$gte":${firstValue},"$lte":${secondValue}}`;
      } else {
        return `"${field}":{"$lte":${firstValue},"$gte":${secondValue}}`;
      }
    } else {
      return '';
    }
  }
  return '';
};
